import React, { useState, useMemo } from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import CommonPage from "@icarius-common/commonPage";
import paths from "@icarius-router/paths";
import { getColumnDefByPage } from "@icarius-table/columnDefs";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import StyledHelpMenu from "@icarius-common/styledMenu";
import MenuItemWithIcon from "@icarius-common/MenuItemWithIcon";
import { CheckIcon } from "@icarius-icons/index";
import DuplicateSkillDialog from "./dialogs/duplicateSkillDialog";
import DuplicateGroupDialog from "./dialogs/duplicateGroupDialog";
import ViewDialog from "./dialogs/viewDialog";
import Form from "./form";
import useABM from "./useABM";

const TypesOfSkillsSpecific = ({ handleOpenParameterPage, handleGoBack }) => {

  const {
    isLoading,
    data,
    dateFormat,
    state,
    handlers,
    showWeightSpecificSkills,
    skillClassList,
  } = useABM();

  const ownColumnDef = useMemo(() => {
    const baseColumnDef = getColumnDefByPage(paths.typesOfSkillsSpecific);
    if (showWeightSpecificSkills) {
      return baseColumnDef;
    }
    return baseColumnDef.filter(col => col.headerName !== getLocalizedString("competencyWeight"));
  }, [showWeightSpecificSkills]);

  const [duplicateMenuAnchor, setDuplicateMenuAnchor] = useState(null);

  const goBackItem = () =>
    <CustomIconButton
      title={getLocalizedString("goBack")}
      onClick={handleGoBack}
      type={"goBack"}
    />

  const addItem = () =>
    <CustomIconButton
      title={getLocalizedString("create")}
      onClick={() => handlers.handleOpenCreateDialog()}
      type={"add"}
    />

  const deleteItem = (gridRef) =>
    <CustomIconButton
      title={getLocalizedString("delete")}
      onClick={() => handlers.handleOpenDeleteDialog(gridRef)}
      type={"delete"}
    />

  const editItem = (gridRef) =>
    <CustomIconButton
      title={getLocalizedString("edit")}
      onClick={() => handlers.handleOpenEditDialog(gridRef)}
      type={"edit"}
    />

  const verifyButton = (gridRef) => showWeightSpecificSkills && (
    <CustomIconButton
      title={getLocalizedString("checkWeight")}
      onClick={() => handlers.handleVerify(gridRef)}
    >
      <CheckIcon />
    </CustomIconButton>
  )

  const duplicateItem = (gridRef) => Boolean(data?.length) &&
    <>
      <CustomIconButton
        title={getLocalizedString("pollDuplication")}
        onClick={(e) => setDuplicateMenuAnchor(e.currentTarget)}
        type={'duplicate'}
      />
      <StyledHelpMenu
        anchorEl={duplicateMenuAnchor}
        isOpen={Boolean(duplicateMenuAnchor)}
        onClose={() => setDuplicateMenuAnchor(null)}
      >
        <MenuItemWithIcon
          text={getLocalizedString("duplicateCompetency")}
          onClick={() => {
            handlers.handleOpenDuplicateSkillDialog(gridRef);
            setDuplicateMenuAnchor(null);
          }}
        />
        <MenuItemWithIcon
          text={getLocalizedString("duplicateByCompetencyGroup")}
          onClick={() => {
            handlers.handleOpenDuplicateGroup();
            setDuplicateMenuAnchor(null);
          }}
        />
      </StyledHelpMenu>
    </>

  const handleRowClick = (row) => {
    handleOpenParameterPage(row.data);
  }

  return (
    <>
      {
        state.createEditFormIsOpen ?
          <Form
            isLoading={isLoading}
            data={state.selectedItem}
            handleClose={handlers.handleCloseEditDialog}
            showVerificationMessage={handlers.showVerificationMessage}
            showWeightSpecificSkills={showWeightSpecificSkills}
            skillClassList={skillClassList}
          />
          :
          <CommonPage
            isLoading={isLoading}
            columnDefPage={paths.typesOfSkillsSpecific}
            ownColumnDef={ownColumnDef}
            title={getLocalizedString("typesOfSkillsSpecific")}
            gridTitle={getLocalizedString("typesOfSkillsSpecific")}
            menuItems={[goBackItem, addItem, editItem, deleteItem, duplicateItem, verifyButton]}
            rowData={data}
            dateFormat={dateFormat}
            handleRowClick={handleRowClick}
            hasExpand
            hasHelp
          >
            {
              state.duplicateSkillDialogIsOpen &&
              <DuplicateSkillDialog
                open={state.duplicateSkillDialogIsOpen}
                data={state.selectedItem}
                isLoading={isLoading}
                handleConfirm={handlers.handleDuplicateSkill}
                handleClose={handlers.handleCloseDuplicateSkillDialog}
              />
            }
            {
              state.duplicateGroupDialogIsOpen &&
              <DuplicateGroupDialog
                open={state.duplicateGroupDialogIsOpen}
                data={data}
                isLoading={isLoading}
                handleConfirm={handlers.handleDuplicateGroup}
                handleClose={handlers.handleCloseDuplicateGroupDialog}
              />
            }
            {
              state.viewDialogIsOpen &&
              <ViewDialog
                open={state.viewDialogIsOpen}
                data={state.selectedItem}
                handleClose={handlers.handleCloseViewDialog}
              />
            }
          </CommonPage>
      }
    </>
  );
}

export default TypesOfSkillsSpecific;